import { useEffect, useId, type ReactNode } from "react";
import { FloatingPortal, FloatingArrow } from "@floating-ui/react";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { cx } from "@/utils/ui/uiHelpers";
import {
  useFloatingOverlay,
  type FloatingDelay,
  type FloatingPlacement,
  type FloatingTrigger,
} from "@/utils/ui/floatingOverlay";
import {
  FLOATING_PORTAL_ID,
  injectFloatingPortalStyles,
} from "@/utils/ui/floatingPortalStyles";

export type TooltipSize = "sm" | "md" | "lg";

const WIDTH_CLASSES: Record<TooltipSize, string> = {
  sm: "max-w-xs",
  md: "max-w-sm",
  lg: "max-w-md",
};

type TooltipProps = React.PropsWithChildren<{
  /** Tooltip content to display */
  content: ReactNode;
  /** Trigger element. If omitted, a default info icon is rendered */
  children?: ReactNode;
  /** Placement relative to the trigger element */
  placement?: FloatingPlacement;
  /** Additional CSS classes for the tooltip panel */
  className?: string;
  /** Hover delay in ms. Single number or [openDelay, closeDelay] tuple */
  delay?: FloatingDelay;
  /** Trigger type: "hover" (default), "click", or "hover-click" */
  trigger?: FloatingTrigger;
  /** Wrapper class for the default trigger icon */
  triggerClassName?: string;
  /** Class for the default info icon */
  iconClassName?: string;
  /** Show arrow pointing to trigger */
  arrow?: boolean;
  /** Additional classes for the arrow SVG */
  arrowClassName?: string;
  /** Controlled open state */
  open?: boolean;
  /** Uncontrolled initial state */
  defaultOpen?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Enable hover-interactive behavior (keeps tooltip open while hovering it) */
  interactive?: boolean;
  /** Safe polygon buffer around hover gap (only when interactive && hover) */
  safeBorder?: number;
  /** Gap between trigger and tooltip in pixels */
  offset?: number;
  size?: TooltipSize;
}>;

const DEFAULT_TOOLTIP_CLASSES = [
  "poppins text-xs leading-snug",
  "bg-gray-100 dark:bg-zinc-800",
  "text-black dark:text-gray-50",
  "border border-gray-300 dark:border-zinc-700",
  "rounded shadow-lg", // nice subtle shadow
  "p-2",
  "max-w-md",
  "whitespace-normal break-words", // wrap long text
].join(" ");

const DEFAULT_TRIGGER_WRAPPER_CLASSES = "inline-flex items-center";
const DEFAULT_ICON_CLASSES = "size-4 text-zinc-600 dark:text-gray-50 ml-2";

/**
 * Tooltip component backed by @floating-ui/react.
 *
 * Provides accessible tooltips with automatic positioning,
 * collision detection, and optional arrow indicators.
 *
 * @example
 * // With custom trigger
 * <Tooltip content="Helpful tip">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * @example
 * // With default info icon trigger
 * <Tooltip content="More information" />
 */
const Tooltip = ({
  content,
  children,
  placement = "auto",
  className,
  delay = 0,
  trigger = "hover",
  arrow = false,
  triggerClassName,
  iconClassName,
  arrowClassName,
  open,
  defaultOpen,
  onOpenChange,
  interactive = false,
  safeBorder = 12,
  offset = 8,
  size = "sm",
}: TooltipProps) => {
  const tooltipId = useId();

  // Inject portal styles once on first mount
  useEffect(() => {
    injectFloatingPortalStyles();
  }, []);

  const {
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    arrowRef,
    isMounted,
    transitionStyles,
  } = useFloatingOverlay({
    open,
    defaultOpen,
    onOpenChange,
    placement,
    trigger,
    delay,
    offset,
    withArrow: arrow,
    role: "tooltip",
    interactive,
    safeBorder,
  });

  // Default to info icon if no children provided
  const triggerNode = children ?? (
    <span className={cx(DEFAULT_TRIGGER_WRAPPER_CLASSES, triggerClassName)}>
      <IoMdInformationCircleOutline
        className={cx(DEFAULT_ICON_CLASSES, iconClassName)}
        aria-hidden="true"
      />
    </span>
  );

  const tooltipWidth = WIDTH_CLASSES[size ?? "md"];

  return (
    <>
      {/* 
        Using a span wrapper ensures consistent inline behavior.
        The ref and props are spread onto this wrapper.
      */}
      <span
        ref={refs.setReference}
        {...getReferenceProps({
          // a11y: link trigger → tooltip for screen readers
          "aria-describedby": isMounted ? tooltipId : undefined,
        })}
        className="inline-flex"
      >
        {triggerNode}
      </span>

      {isMounted && (
        <FloatingPortal id={FLOATING_PORTAL_ID}>
          {/* 
            Positioning wrapper - receives floatingStyles for placement.
            This is separate from the content wrapper to avoid transform conflicts.
          */}
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              // Non-interactive tooltips shouldn't intercept pointer events
              pointerEvents: interactive && context.open ? "auto" : "none",
            }}
            {...getFloatingProps()}
            className="z-[9999]"
          >
            {/* Content wrapper - receives transition styles for animations */}
            <div
              id={tooltipId}
              role="tooltip"
              style={transitionStyles}
              className={cx(DEFAULT_TOOLTIP_CLASSES, tooltipWidth, className)}
            >
              {arrow && (
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  className={cx(
                    "fill-gray-100 dark:fill-zinc-800 stroke-gray-300 dark:stroke-zinc-700",
                    arrowClassName,
                  )}
                />
              )}
              {content}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default Tooltip;
